import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../..';
import {
  checkConcurrentTenancyRequested,
  clearConcurrentTenancyCheck,
  CHECK_CONCURRENT_TENANCY_STORE_KEY,
  addTenantOnRepositRequested,
} from '../../../redux/reposit/reposit.actions';
import {
  getConcurrentTenancyCheckResult,
  getConcurrentTenancyCheckError,
  getConcurrentTenancyCheckedEmail,
} from '../../../redux/reposit/reposit.selectors';
import { createLoadingSelector } from '../../../redux/loading/loading.selector';
import { analyzeConcurrentTenancy } from './concurrentTenancyUtils';

interface UseConcurrentTenancyCheckOptions {
  tenancyId: string;
  organizationId: string;
  tenancyStartDate: string;
}

export const useConcurrentTenancyCheck = ({
  tenancyId,
  organizationId,
  tenancyStartDate,
}: UseConcurrentTenancyCheckOptions) => {
  const dispatch = useDispatch<AppDispatch>();
  const isCheckingSelector = createLoadingSelector([CHECK_CONCURRENT_TENANCY_STORE_KEY]);
  const isChecking = useSelector(isCheckingSelector);

  const checkResult = useSelector(getConcurrentTenancyCheckResult);
  const checkError = useSelector(getConcurrentTenancyCheckError);
  const checkedEmail = useSelector(getConcurrentTenancyCheckedEmail);

  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const checkConcurrentTenancy = useCallback(
    (email: string) => {
      setPendingEmail(email);
      dispatch(
        checkConcurrentTenancyRequested({
          organizationId,
          startDate: tenancyStartDate,
          email,
        })
      );
    },
    [dispatch, organizationId, tenancyStartDate]
  );

  const clearCheck = useCallback(() => {
    dispatch(clearConcurrentTenancyCheck());
  }, [dispatch]);

  const addTenantDirectly = useCallback(
    (email: string) => {
      dispatch(
        addTenantOnRepositRequested({
          tenancyId,
          newTenantEmail: email,
        })
      );
    },
    [dispatch, tenancyId]
  );

  // Handle check completion
  useEffect(() => {
    if (!pendingEmail || isChecking) {
      return;
    }

    // Check completed
    if (checkError) {
      // Check failed - error will be displayed inline
      setPendingEmail(null);
      return;
    }

    if (checkResult && checkedEmail === pendingEmail) {
      const analysis = analyzeConcurrentTenancy(checkResult, pendingEmail);
      if (analysis.isBlocked) {
        // Tenant is blocked - block will be displayed inline
        setPendingEmail(null);
        return;
      }

      // Not blocked - proceed with adding tenant automatically
      addTenantDirectly(pendingEmail);
      setPendingEmail(null);
    }
  }, [checkResult, checkError, checkedEmail, pendingEmail, isChecking, addTenantDirectly]);

  return {
    checkResult,
    checkError,
    checkedEmail,
    isChecking,
    checkConcurrentTenancy,
    clearCheck,
    addTenantDirectly,
  };
};
